<template>
    <!-- 86bc70q8c: every self credit check starts with why. The answer is
         required, and "Other" needs a short explanation. 86bce6r42: so does
         the landlord-view reason, and every report also asks the renter to
         acknowledge the cost and say what price they think is fair.
         86bcevwqm: reason, then fair price, then the acknowledgement, each
         shown only once the one before it is answered. 86bcfa7n0: the fair
         price is typed, in whole dollars from $1 to $999. -->
    <form class="w-full max-w-md space-y-3 text-left" data-test="self-credit-check-purpose-form" @submit.prevent="submit">
        <div>
            <label for="self_credit_check_purpose" class="block text-sm font-semibold text-gray-900 mb-2">
                Why are you pulling your own report today?
            </label>
            <select
                id="self_credit_check_purpose"
                v-model="form.purpose"
                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400 sm:text-sm"
            >
                <option value="" disabled>Select a reason</option>
                <option v-for="option in options" :key="option.value" :value="option.value">
                    {{ option.label }}
                </option>
            </select>
            <p v-if="form.errors.purpose" class="mt-1 text-sm text-red-600">
                {{ form.errors.purpose }}
            </p>
        </div>

        <div v-if="needsExplanation">
            <label for="self_credit_check_purpose_other" class="sr-only">{{ explanationPrompt }}</label>
            <textarea
                id="self_credit_check_purpose_other"
                v-model="form.purpose_other"
                rows="3"
                maxlength="1000"
                :placeholder="explanationPrompt"
                class="block w-full rounded-md border-gray-300 shadow-sm placeholder:text-gray-400 focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400 sm:text-sm"
            />
            <p v-if="form.errors.purpose_other" class="mt-1 text-sm text-red-600">
                {{ form.errors.purpose_other }}
            </p>
        </div>

        <div v-if="reasonAnswered">
            <label for="self_credit_check_fair_price" class="block text-sm font-semibold text-gray-900 mb-2">
                I believe this is a fair price to pay for my personalized report:
            </label>
            <div class="relative">
                <span class="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 text-sm">$</span>
                <input
                    id="self_credit_check_fair_price"
                    :value="form.fair_price"
                    type="text"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    autocomplete="off"
                    placeholder="Enter an amount"
                    class="block w-full pl-7 rounded-md border-gray-300 shadow-sm placeholder:text-gray-400 focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400 sm:text-sm"
                    data-test="fair-price"
                    @input="updateFairPrice"
                >
            </div>
            <p v-if="fairPriceError" class="mt-1 text-sm text-red-600" data-test="fair-price-error">
                {{ fairPriceError }}
            </p>
        </div>

        <fieldset v-if="fairPriceAnswered">
            <legend class="block text-sm font-semibold text-gray-900 mb-2">
                I acknowledge there are costs associated with pulling this custom report.
            </legend>
            <label class="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                    v-model="form.cost_acknowledged"
                    type="checkbox"
                    class="rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                    data-test="cost-acknowledged"
                >
                Yes
            </label>
            <p v-if="form.errors.cost_acknowledged" class="mt-1 text-sm text-red-600">
                {{ form.errors.cost_acknowledged }}
            </p>
        </fieldset>

        <div class="flex justify-center gap-2 pt-2">
            <button
                v-if="cancellable"
                type="button"
                class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200"
                @click="emit('cancel')"
            >
                Cancel
            </button>
            <button type="submit" class="primaryButton disabled:opacity-50 disabled:cursor-not-allowed" :disabled="!canSubmit">
                Start New Report
            </button>
        </div>
    </form>
</template>

<script setup>
import { computed, watch } from 'vue'
import { useForm } from '@inertiajs/vue3'

defineProps({
    options: {
        type: Array,
        required: true,
    },
    // Shown in the header popup, which otherwise only closes on Escape or a click outside.
    cancellable: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['cancel'])

const OTHER = 'other'

const LANDLORD_VIEW = 'landlord_view'

const form = useForm({
    purpose: '',
    purpose_other: '',
    cost_acknowledged: false,
    fair_price: '',
})

const needsExplanation = computed(() => [OTHER, LANDLORD_VIEW].includes(form.purpose))

const explanationPrompt = computed(() => (form.purpose === LANDLORD_VIEW ? 'Please explain why' : 'Please explain'))

const reasonAnswered = computed(() => !!form.purpose && (!needsExplanation.value || form.purpose_other.trim() !== ''))

// Whole dollars, $1 to $999 — the same rule the server enforces.
const FAIR_PRICE = /^[1-9][0-9]{0,2}$/

const fairPriceAnswered = computed(() => reasonAnswered.value && FAIR_PRICE.test(form.fair_price))

const fairPriceError = computed(() => {
    if (form.errors.fair_price) {
        return form.errors.fair_price
    }

    return form.fair_price !== '' && !FAIR_PRICE.test(form.fair_price)
        ? 'Please enter a whole dollar amount between $1 and $999.'
        : ''
})

// Whole dollars: anything from a decimal point on is dropped, and so is a
// "$", comma or space. An amount out of range stays as typed and is flagged,
// never quietly cut to a different number.
function updateFairPrice(event) {
    const digits = event.target.value.split('.')[0].replace(/\D/g, '')
    form.fair_price = digits
    event.target.value = digits
}

// A later answer only counts for the answers above it, so undoing an earlier
// one clears what comes after instead of bringing it back already filled in.
watch(reasonAnswered, (answered) => {
    if (!answered) {
        form.fair_price = ''
        form.cost_acknowledged = false
    }
})

watch(fairPriceAnswered, (answered) => {
    if (!answered) {
        form.cost_acknowledged = false
    }
})

const canSubmit = computed(() => !form.processing && fairPriceAnswered.value && form.cost_acknowledged)

function submit() {
    if (!canSubmit.value) {
        return
    }

    form.post(route('reports.store'))
}
</script>
